import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@config/msalAuthConfig';
import { AuthProvider, useAuth } from '@contexts/AuthContext';
import { ROLES, ROLE_GROUPS } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { LoadingProvider } from '@contexts/LoadingContext';
import { SocketProvider } from '@contexts/SocketContext';
import ToastContainer from '@components/common/ToastContainer';
import ErrorBoundary from '@components/common/ErrorBoundary';
import LoginForm from '@components/features/auth/LoginForm';
import AuthRedirectHandler from '@components/auth/AuthRedirectHandler';
import ProtectedRoute from '@components/common/ProtectedRoute';
import DashboardPage from '@pages/DashboardPage';
import AssessorDashboardPage from '@pages/AssessorDashboardPage';
import SchedulerDashboardPage from '@pages/SchedulerDashboardPage';
import ExecutiveDashboardPage from '@pages/ExecutiveDashboardPage';
import ProgramLeadDashboardPage from '@pages/ProgramLeadDashboardPage';
import AssessmentsPage from '@pages/AssessmentsPage';
import AssessmentDetailPage from '@pages/AssessmentDetailPage';
import AssessmentDetailAdminPage from '@pages/AssessmentDetailAdminPage';
import AssessorMeetingsPage from '@pages/AssessorMeetingsPage';
import SuppliersPage from '@pages/SuppliersPage';
import SupplierDetailPage from '@pages/SupplierDetailPage';
import ContractsPage from '@pages/ContractsPage';
import ContractDetailPage from '@pages/ContractDetailPage';
import EvidenceManagementPage from '@pages/EvidenceManagementPage';
import EvidenceUploadPage from '@pages/EvidenceUploadPage';
import EvidenceReviewPage from '@pages/EvidenceReviewPage';
import ReportsPage from '@pages/ReportsPage';
import MeetingDetailPage from '@pages/MeetingDetailPage';
import UserManagementPage from '@pages/UserManagementPage';
import AdminDataManagementPage from '@pages/AdminDataManagementPage';
import ImportJobDetailPage from '@pages/ImportJobDetailPage';
import DivisionManagementPage from '@pages/DivisionManagementPage';
import NotificationSettingsPage from '@pages/NotificationSettingsPage';
import HelpPage from '@pages/HelpPage';
import HelpArticlePage from '@pages/HelpArticlePage';
import DivisionConflictWorklistPage from '@pages/DivisionConflictWorklistPage';
import MissingContractRoleWorklistPage from '@pages/MissingContractRoleWorklistPage';
import ContractRoleDashboardPage from '@pages/ContractRoleDashboardPage';
import QueriesPage from '@pages/QueriesPage';
import QueryBuilderPage from '@pages/QueryBuilderPage';
import QueryResultsPage from '@pages/QueryResultsPage';
import { isMsalProvider } from '@config/authConfig';
import { getApiErrorStatus } from '@utils/apiError';
import './styles/index.css';

// Create a query client for React Query with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry 4xx responses (e.g. 401/403/404/429) - retrying won't change
      // the outcome and, for 429, would fight the rate limiter. Retry once for
      // everything else (5xx/network errors).
      retry: (failureCount, error) => {
        const status = getApiErrorStatus(error);
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Prevent refetching when component mounts if data exists
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      gcTime: 10 * 60 * 1000 // Keep data in cache for 10 minutes (renamed from cacheTime)
    }
  }
});

// Create MSAL instance outside of component to prevent re-renders
const msalInstance = isMsalProvider ? new PublicClientApplication(msalConfig) : null;

// Dashboard selector based on user role
// Multi-role: Use priority order (Executive > Program Lead > Contract Role > Assessor > default)
const DashboardSelector: React.FC = () => {
  const { user } = useAuth();

  // Executive takes precedence for dashboard view
  if (hasRole(user, ROLES.EXECUTIVE)) {
    return <ExecutiveDashboardPage />;
  }

  // Program Lead sees dedicated launch page
  if (hasRole(user, ROLES.PROGRAM_LEAD)) {
    return <ProgramLeadDashboardPage />;
  }

  // Contract Role sees the assigned-contracts dashboard
  if (hasRole(user, ROLES.CONTRACT_ROLE)) {
    return <ContractRoleDashboardPage />;
  }

  // Assessor sees assessor dashboard
  if (hasRole(user, ROLES.ASSESSOR)) {
    return <AssessorDashboardPage />;
  }

  return <DashboardPage />;
};

// Assessment Detail selector based on user role
const AssessmentDetailSelector: React.FC = () => {
  const { user } = useAuth();

  if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) {
    return <AssessmentDetailAdminPage />;
  }

  return <AssessmentDetailPage />;
};

// Authenticated app wrapper to include Socket.io and FloatingChat
const AuthenticatedApp: React.FC = () => {
  return (
    <>
      <AuthRedirectHandler />
      <Routes>
        <Route path="/login" element={<LoginForm />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requirePermission="canAccessDashboard">
              <ErrorBoundary level="page">
                <DashboardSelector />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessments"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <AssessmentsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessments/:id"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <AssessmentDetailSelector />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute requirePermission="canAccessSuppliers">
              <ErrorBoundary level="page">
                <SuppliersPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers/:id"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <SupplierDetailPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts"
          element={
            <ProtectedRoute requirePermission="canAccessContracts">
              <ErrorBoundary level="page">
                <ContractsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts/:id"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <ContractDetailPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/evidence"
          element={
            <ProtectedRoute requirePermission="isContractRole">
              <ErrorBoundary level="page">
                <EvidenceManagementPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/evidence/upload/:itemId"
          element={
            <ProtectedRoute requireOneOfPermissions={['isContractRole', 'isAssessor']}>
              <ErrorBoundary level="page">
                <EvidenceUploadPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/evidence-review"
          element={
            <ProtectedRoute requirePermission="isAssessor">
              <ErrorBoundary level="page">
                <EvidenceReviewPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessor/meetings"
          element={
            <ProtectedRoute requireOneOfPermissions={['isAssessor', 'isAdmin']}>
              <ErrorBoundary level="page">
                <AssessorMeetingsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requirePermission="canAccessUsers">
              <ErrorBoundary level="page">
                <UserManagementPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/divisions"
          element={
            <ProtectedRoute requirePermission="canAccessDivisions">
              <ErrorBoundary level="page">
                <DivisionManagementPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/conflicts/division"
          element={
            <ProtectedRoute requirePermission="isAdmin">
              <ErrorBoundary level="page">
                <DivisionConflictWorklistPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/conflicts/missing-contract-role"
          element={
            <ProtectedRoute requirePermission="isAdmin">
              <ErrorBoundary level="page">
                <MissingContractRoleWorklistPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        {/* Email Templates moved into a tab on /admin/notifications; redirect old bookmarks. */}
        <Route path="/admin/email-templates" element={<Navigate to="/admin/notifications" replace />} />
        <Route
          path="/admin/notifications"
          element={
            <ProtectedRoute requirePermission="isAdmin">
              <ErrorBoundary level="page">
                <NotificationSettingsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        {/* History moved into a tab on /admin/notifications; redirect old bookmarks. */}
        <Route path="/admin/notification-history" element={<Navigate to="/admin/notifications" replace />} />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <HelpPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help/:id"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <HelpArticlePage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requirePermission="canAccessReports">
              <ErrorBoundary level="page">
                <ReportsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scheduler"
          element={
            <ProtectedRoute requirePermission="canAccessScheduler">
              <ErrorBoundary level="page">
                <SchedulerDashboardPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        {/* Import Jobs moved into a tab on /admin/data-management; redirect old bookmarks. */}
        <Route path="/admin/import-jobs" element={<Navigate to="/admin/data-management?tab=import-jobs" replace />} />
        <Route
          path="/admin/import-jobs/:id"
          element={
            <ProtectedRoute requirePermission="isAdmin">
              <ErrorBoundary level="page">
                <ImportJobDetailPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/data-management"
          element={
            <ProtectedRoute requirePermission="isAdmin">
              <ErrorBoundary level="page">
                <AdminDataManagementPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contract-assessment-meetings/:id"
          element={
            <ProtectedRoute>
              <ErrorBoundary level="page">
                <MeetingDetailPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/queries"
          element={
            <ProtectedRoute requirePermission="canAccessQueries">
              <ErrorBoundary level="page">
                <QueriesPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/queries/new"
          element={
            <ProtectedRoute requirePermission="canAccessQueries">
              <ErrorBoundary level="page">
                <QueryBuilderPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/queries/:id/edit"
          element={
            <ProtectedRoute requirePermission="canAccessQueries">
              <ErrorBoundary level="page">
                <QueryBuilderPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/queries/:id/results"
          element={
            <ProtectedRoute requirePermission="canAccessQueries">
              <ErrorBoundary level="page">
                <QueryResultsPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
};

function App() {
  const appTree = (
    <ErrorBoundary level="global">
      <QueryClientProvider client={queryClient}>
        <AuthProvider queryClient={queryClient}>
          <LoadingProvider>
            <SocketProvider>
              <Router>
                <div className="App">
                  <AuthenticatedApp />
                </div>
                <ToastContainer />
              </Router>
            </SocketProvider>
          </LoadingProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );

  if (isMsalProvider && msalInstance) {
    return <MsalProvider instance={msalInstance}>{appTree}</MsalProvider>;
  }

  return appTree;
}

export default App;
